import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && user) navigate("/admin", { replace: true });
  }, [user, isLoading, navigate]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!email || !password) { setError("Please enter email and password."); return; }
    setLoading(true); setError("");
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid credentials.");
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#0A0A0A] flex flex-col">
      {/* Background */}
      <div className="fixed inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=1920&q=80"
          alt=""
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-[#0A0A0A]/90" />
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <div className="text-center mb-10">
            <Link to="/" className="inline-flex items-center gap-2 mb-8">
              <span className="font-heading text-2xl font-light text-white tracking-widest">Travelorza</span>
              <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C]" />
            </Link>
            <p className="overline mb-2">Internal Access</p>
            <h1 className="font-heading text-3xl font-light text-white">Admin Dashboard</h1>
          </div>

          <form
            data-testid="admin-login-form"
            onSubmit={handleSubmit}
            className="glass rounded-xl p-8 space-y-6"
          >
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Email</label>
              <input
                data-testid="admin-login-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@travelorza.com"
                className="w-full bg-transparent border-b border-white/20 focus:border-[#C9A84C] outline-none text-white placeholder:text-white/25 py-2 text-sm transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Password</label>
              <input
                data-testid="admin-login-password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-transparent border-b border-white/20 focus:border-[#C9A84C] outline-none text-white placeholder:text-white/25 py-2 text-sm transition-colors"
              />
            </div>

            {error && (
              <div data-testid="admin-login-error" className="flex items-center gap-2 text-red-400 text-xs">
                <AlertCircle size={14} />
                {error}
              </div>
            )}

            <button
              data-testid="admin-login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full btn-gold py-4 text-xs disabled:opacity-50"
            >
              {loading ? "Authenticating..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
